/**
 * Freesound Browser Component
 * Search and preview sounds from Freesound.org's 500k+ library
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import {
  Search,
  Play,
  Pause,
  X,
  Loader2,
  Star,
  Download,
  User,
  Clock,
  ExternalLink,
  Volume2,
  Filter,
  ChevronDown,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Freesound categories
const FREESOUND_CATEGORIES = [
  { id: 'drums', name: 'Drums & Percussion', icon: '🥁' },
  { id: 'bass', name: 'Bass', icon: '🎸' },
  { id: 'synth', name: 'Synths & Pads', icon: '🎹' },
  { id: 'vocals', name: 'Vocals', icon: '🎤' },
  { id: 'fx', name: 'Sound Effects', icon: '✨' },
  { id: 'guitar', name: 'Guitar', icon: '🎸' },
  { id: 'piano', name: 'Piano & Keys', icon: '🎹' },
  { id: 'strings', name: 'Strings & Orchestra', icon: '🎻' },
  { id: 'ambient', name: 'Ambient & Textures', icon: '🌊' },
  { id: 'loops', name: 'Loops & Beats', icon: '🔄' },
];

// Freesound sample card
const FreesoundCard = ({ sample, isPlaying, onPlay, onStop, onDragStart }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={`group p-3 rounded-lg border transition-all cursor-pointer ${
      isPlaying
        ? 'bg-orange-500/20 border-orange-500/50'
        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-500'
    }`}
    draggable
    onDragStart={(e) => onDragStart?.(e, sample)}
    onClick={() => isPlaying ? onStop() : onPlay(sample)}
  >
    {/* Waveform image if available */}
    {sample.waveform_url && (
      <div className="mb-2 h-12 rounded overflow-hidden bg-zinc-900">
        <img 
          src={sample.waveform_url} 
          alt="waveform" 
          className="w-full h-full object-cover opacity-60"
        />
      </div>
    )}
    
    <div className="flex items-start justify-between mb-1">
      <div className="flex-1 min-w-0">
        <h4 className="text-sm font-medium text-white truncate">{sample.name}</h4>
        <p className="text-xs text-zinc-500 flex items-center gap-1">
          <User className="w-3 h-3" />
          {sample.username}
        </p>
      </div>
      <button
        onClick={(e) => {
          e.stopPropagation();
          isPlaying ? onStop() : onPlay(sample);
        }}
        className={`p-1.5 rounded-full transition-all ${
          isPlaying 
            ? 'bg-orange-500 text-white' 
            : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600 hover:text-white'
        }`}
      >
        {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
      </button>
    </div>
    
    {/* Tags */}
    {sample.tags && sample.tags.length > 0 && (
      <div className="flex flex-wrap gap-1 mb-2">
        {sample.tags.slice(0, 3).map((tag, i) => (
          <span key={i} className="px-1.5 py-0.5 bg-zinc-700/50 rounded text-[10px] text-zinc-400">
            {tag}
          </span>
        ))}
      </div>
    )}
    
    {/* Stats */}
    <div className="flex items-center justify-between text-xs text-zinc-500">
      <div className="flex items-center gap-2">
        <span className="flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {sample.duration}s
        </span>
        {sample.rating > 0 && (
          <span className="flex items-center gap-1">
            <Star className="w-3 h-3 text-amber-400" />
            {sample.rating.toFixed(1)}
          </span>
        )}
      </div>
      <span className="flex items-center gap-1">
        <Download className="w-3 h-3" />
        {sample.downloads?.toLocaleString()}
      </span>
    </div>
    
    {/* Play progress */}
    {isPlaying && (
      <div className="mt-2 h-1 bg-zinc-700 rounded-full overflow-hidden">
        <motion.div
          className="h-full bg-orange-500"
          initial={{ width: 0 }}
          animate={{ width: '100%' }}
          transition={{ duration: sample.duration || 5, ease: 'linear' }}
        />
      </div>
    )}
  </motion.div>
);

export const FreesoundBrowser = ({ onInsertSample }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [samples, setSamples] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [playingSampleId, setPlayingSampleId] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [maxDuration, setMaxDuration] = useState(30);
  
  const audioRef = useRef(null);

  // Search Freesound
  const searchFreesound = useCallback(async (query, pageNum = 1) => {
    if (!query.trim()) return;
    
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        query: query,
        page: pageNum,
        page_size: 15,
        filter: `duration:[0 TO ${maxDuration}]`
      });
      
      const response = await fetch(`${API_URL}/api/freesound/search?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setSamples(data.samples);
        setTotalPages(data.pages);
        setTotalCount(data.count);
        setPage(pageNum);
      }
    } catch (err) {
      console.error('Freesound search error:', err);
      toast.error('Failed to search Freesound');
    } finally {
      setIsLoading(false);
    }
  }, [maxDuration]);

  // Browse category
  const browseCategory = useCallback(async (category, pageNum = 1) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: pageNum,
        page_size: 15,
        max_duration: maxDuration
      });
      
      const response = await fetch(`${API_URL}/api/freesound/browse/${category}?${params}`);
      const data = await response.json();
      
      if (data.success) {
        setSamples(data.samples);
        setTotalPages(data.pages);
        setTotalCount(data.count);
        setPage(pageNum);
      }
    } catch (err) {
      console.error('Freesound browse error:', err);
      toast.error('Failed to browse category');
    } finally {
      setIsLoading(false);
    }
  }, [maxDuration]);

  // Handle category selection
  const handleCategorySelect = (categoryId) => {
    if (selectedCategory === categoryId) {
      setSelectedCategory(null);
      setSamples([]);
    } else {
      setSelectedCategory(categoryId);
      setSearchQuery('');
      browseCategory(categoryId, 1);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setSelectedCategory(null);
      searchFreesound(searchQuery, 1);
    }
  };

  // Play sample
  const playSample = useCallback(async (sample) => {
    // Stop current playback
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    
    if (!sample.preview_url) {
      toast.error('No preview available for this sound');
      return;
    }
    
    setPlayingSampleId(sample.id);
    
    try {
      const audio = new Audio(sample.preview_url);
      audio.volume = 0.7;
      audioRef.current = audio;
      
      audio.onended = () => {
        setPlayingSampleId(null);
        audioRef.current = null;
      };
      
      audio.onerror = () => {
        toast.error('Failed to play sample');
        setPlayingSampleId(null);
      };
      
      await audio.play();
    } catch (err) {
      console.error('Playback error:', err);
      setPlayingSampleId(null);
    }
  }, []);

  // Stop playback
  const stopSample = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingSampleId(null);
  }, []);

  // Handle drag
  const handleDragStart = (e, sample) => {
    e.dataTransfer.setData('application/json', JSON.stringify({
      type: 'freesound-sample',
      sample: sample
    }));
    e.dataTransfer.effectAllowed = 'copy';
  };

  // Pagination
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    
    if (selectedCategory) {
      browseCategory(selectedCategory, newPage);
    } else if (searchQuery) {
      searchFreesound(searchQuery, newPage);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search Bar */}
      <form onSubmit={handleSearch} className="p-3 border-b border-zinc-800">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search 500k+ sounds on Freesound..."
            className="w-full pl-9 pr-20 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder:text-zinc-500 focus:border-orange-500 focus:outline-none"
          />
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              className={`p-1 ${showFilters ? 'text-orange-400' : 'text-zinc-400'}`}
            >
              <Filter className="w-4 h-4" />
            </Button>
            <Button
              type="submit"
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 px-2 py-1 text-xs"
            >
              Search
            </Button>
          </div>
        </div>
        
        {/* Filters */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="mt-2 overflow-hidden"
            >
              <div className="flex items-center gap-3 text-sm">
                <label className="text-zinc-400">Max duration:</label>
                <select
                  value={maxDuration}
                  onChange={(e) => setMaxDuration(Number(e.target.value))}
                  className="bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-white text-xs"
                >
                  <option value={5}>5 sec</option>
                  <option value={10}>10 sec</option>
                  <option value={30}>30 sec</option>
                  <option value={60}>1 min</option>
                  <option value={300}>5 min</option>
                </select>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </form>
      
      {/* Categories */}
      <div className="p-3 border-b border-zinc-800">
        <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-thin">
          {FREESOUND_CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => handleCategorySelect(cat.id)}
              data-testid={`freesound-cat-${cat.id}`}
              className={`px-3 py-1.5 rounded-lg text-sm whitespace-nowrap transition-all ${
                selectedCategory === cat.id
                  ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                  : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700'
              }`}
            >
              <span className="mr-1">{cat.icon}</span>
              {cat.name}
            </button>
          ))}
        </div>
      </div>
      
      {/* Results */}
      <div className="flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
          </div>
        ) : samples.length > 0 ? (
          <>
            {/* Results count */}
            <div className="text-xs text-zinc-500 mb-3">
              {totalCount.toLocaleString()} sounds found
            </div>
            
            {/* Sample grid */}
            <div className="grid grid-cols-1 gap-2">
              {samples.map(sample => (
                <FreesoundCard
                  key={sample.id}
                  sample={sample}
                  isPlaying={playingSampleId === sample.id}
                  onPlay={playSample}
                  onStop={stopSample}
                  onDragStart={handleDragStart}
                />
              ))}
            </div>
            
            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page <= 1}
                  className="text-zinc-400"
                >
                  Previous
                </Button>
                <span className="text-sm text-zinc-500">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page >= totalPages}
                  className="text-zinc-400"
                >
                  Next
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center h-32 text-center">
            <Volume2 className="w-10 h-10 text-zinc-600 mb-2" />
            <p className="text-zinc-500 text-sm">
              {selectedCategory || searchQuery 
                ? 'No sounds found' 
                : 'Search or select a category to browse'}
            </p>
            <p className="text-zinc-600 text-xs mt-1">
              Powered by Freesound.org
            </p>
          </div>
        )}
      </div>
      
      {/* Attribution notice */}
      <div className="p-2 border-t border-zinc-800 text-center">
        <a
          href="https://freesound.org"
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-zinc-500 hover:text-orange-400 inline-flex items-center gap-1"
        >
          Sounds from Freesound.org
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
};

export default FreesoundBrowser;
